/**
 * What a field capture is, before anything tries to send it.
 *
 * The shape matters more than it looks. In Phase 4 these have to survive being
 * written to IndexedDB in a dead zone and replayed when signal returns, so a
 * capture must be **describable without being sent**:
 *
 *  - no closures, no callbacks, no live object references in the payload
 *  - an id assigned by the client, before any request
 *  - everything structured-cloneable (`File` and `Blob` qualify, so a photo
 *    holding its `File` is fine to persist)
 *
 * Building that discipline now is the entire point of this module. Retrofitting
 * it later means touching every screen that writes, after each has grown its own
 * error handling and retry.
 */

export type CaptureKind = 'site' | 'note' | 'photo'

/**
 * `queued` → `sending` → `sent` | `failed`, and `failed` → `sending` on retry.
 *
 * `queued` rather than `pending` deliberately: it is the state a capture sits in
 * offline, and naming it for the queue keeps that meaning once Phase 4 arrives.
 */
export type CaptureStatus = 'queued' | 'sending' | 'sent' | 'failed'

export interface Capture<K extends CaptureKind = CaptureKind, P = unknown> {
	/** Client-assigned, before any request. See the caveat on `submitCapture`. */
	readonly id: string
	readonly kind: K
	readonly createdAt: number
	readonly payload: P
	readonly status: CaptureStatus
	readonly attempts: number
	/** Human-readable, for showing the user. Present only when failed. */
	readonly error?: string
	/** Whatever the handler returned — the created site, the uploaded image. */
	readonly result?: unknown
}

export interface SitePayload {
	name: string
	latitude?: number
	longitude?: number
	address?: string
	city?: string
	state?: string
	postalCode?: string
	gpsAccuracy?: number
	capturedVia?: 'gps' | 'map' | 'address'
}

export interface NotePayload {
	/** HTML, because every note model stores an HTML string. */
	html: string
	siteId?: string
	locationId?: string
	projectId?: string
}

/**
 * An image already in the library, and therefore already on Cloudinary.
 *
 * Structured-cloneable, like everything else in a payload, so a capture that
 * carries one can still be persisted and replayed in Phase 4.
 */
export interface LibraryImageRef {
	/** The library row, so it can be marked used once the note is written. */
	captureImageId: string
	url: string
	uuid?: string
	name?: string
	size?: number
	type?: string
}

export interface PhotoPayload {
	/**
	 * A freshly taken photograph. Absent when `libraryImage` is set.
	 *
	 * Exactly one of the two is present. A library image has already been
	 * compressed and uploaded, so putting it back through that path would spend
	 * a second upload to produce a second copy of a picture that already exists.
	 */
	file?: File
	/** An image pulled from the field library instead of taken now. */
	libraryImage?: LibraryImageRef
	siteId?: string
	locationId?: string
	/**
	 * Plain text, mirrored onto the image row so the gallery's hover overlay
	 * says something. The rich-text version lives on the note.
	 */
	caption?: string
	/** Set alongside `locationId` when the chat/note belongs to a project. */
	projectId?: string
	/** frontage, signage, ingress, parking… Added properly in Phase 2. */
	category?: string
}

/** What a sent photo capture carries back, so the note body can cite it. */
export interface PhotoResult {
	url: string
	imageId?: string
	bytes: number
	originalBytes: number
}

/**
 * A fresh capture, queued and never yet sent.
 *
 * `crypto.randomUUID` where available — every browser this app targets has it —
 * with a fallback so a unit test or an odd environment cannot throw here. The id
 * only has to be unique among this user's in-flight captures.
 */
export const createCapture = <K extends CaptureKind, P>(kind: K, payload: P): Capture<K, P> => ({
	id:
		typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
			? crypto.randomUUID()
			: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
	kind,
	createdAt: Date.now(),
	payload,
	status: 'queued',
	attempts: 0,
})

/* Transitions are pure and return a new capture, so a caller can hold the old
 * one — which is what makes a queue of these safe to reason about. */

export const markSending = <C extends Capture>(capture: C): C => ({
	...capture,
	status: 'sending',
	attempts: capture.attempts + 1,
	error: undefined,
})

export const markSent = <C extends Capture>(capture: C, result?: unknown): C => ({
	...capture,
	status: 'sent',
	result,
	error: undefined,
})

export const markFailed = <C extends Capture>(capture: C, error: string): C => ({
	...capture,
	status: 'failed',
	error,
})

/** Worth retrying? A sent capture never is — that is what stops duplicates. */
export const isRetryable = (capture: Capture): boolean => capture.status === 'failed'
